import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { BRAND_MINT, formatCentsWithCurrency } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { getMyInvoiceDetailApi, getMyBankDetailsApi, payInvoiceApi, downloadInvoicePdfApi, type InvoiceDetail, type EmployerBankDetails } from '@/lib/billing'
import {
  LoaderIcon,
  FileTextIcon,
  BuildingIcon,
  CheckCircleIcon,
  ClockIcon,
  AlertTriangleIcon,
  XCircleIcon,
  HashIcon,
  CalendarIcon,
  UsersIcon,
  BanknoteIcon,
  ChevronLeftIcon,
  CopyIcon,
  CheckIcon,
  LandmarkIcon,
  CreditCardIcon,
  ExternalLinkIcon,
  PlusCircleIcon,
  DownloadIcon,
  StoreIcon,
} from 'lucide-react'

const INVOICE_STATUS_STYLES: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  paid: { label: 'Paid', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  overdue: { label: 'Overdue', color: 'bg-destructive/10 text-destructive' },
  cancelled: { label: 'Cancelled', color: 'bg-muted text-muted-foreground' },
  refunded: { label: 'Refunded', color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400' },
}


function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  })
}

export const Route = createFileRoute('/dashboard/billing/invoices/$invoiceId')({
  component: () => {
    const { invoiceId } = Route.useParams()
    const [detail, setDetail] = useState<InvoiceDetail | null>(null)
    const [bankDetails, setBankDetails] = useState<EmployerBankDetails | null>(null)
    const [loading, setLoading] = useState(true)
    const [paying, setPaying] = useState(false)
    const [copyField, setCopyField] = useState<string | null>(null)

    useEffect(() => {
      let cancelled = false
      async function load() {
        try {
          const [detailRes, bankRes] = await Promise.all([
            getMyInvoiceDetailApi(Number(invoiceId)),
            getMyBankDetailsApi().catch(() => null),
          ])
          if (!cancelled) {
            setDetail(detailRes)
            setBankDetails(bankRes)
          }
        } catch (err) {
          if (!cancelled) {
            toast.error(err instanceof Error ? err.message : 'Failed to load invoice details')
          }
        } finally {
          if (!cancelled) setLoading(false)
        }
      }
      load()
      return () => { cancelled = true }
    }, [invoiceId])

    const handleCopy = async (value: string, field: string) => {
      try {
        await navigator.clipboard.writeText(value)
        setCopyField(field)
        setTimeout(() => setCopyField(null), 2000)
      } catch { /* fallback */ }
    }

    const handleDownloadPdf = async () => {
      if (!detail) return
      try {
        await downloadInvoicePdfApi(Number(invoiceId))
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to download PDF')
      }
    }

    const handlePayWithCard = async () => {
      if (!detail || paying) return
      setPaying(true)
      try {
        const invId = Number(invoiceId)
        const result = await payInvoiceApi(invId)
        window.location.href = result.url
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Unable to start card payment. Please try bank transfer or contact support.')
        setPaying(false)
      }
    }

    if (loading) {
      return (
        <div className="mx-auto max-w-4xl">
          <div className="mb-6">
            <Link to="/dashboard/billing/invoices" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
              <ChevronLeftIcon className="size-3" />Back to invoices
            </Link>
          </div>
          <div className="flex items-center justify-center rounded-xl border border-border bg-card py-16">
            <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
            <p className="ml-2 text-sm text-muted-foreground">Loading invoice…</p>
          </div>
        </div>
      )
    }

    if (!detail) {
      return (
        <div className="mx-auto max-w-4xl">
          <div className="mb-6">
            <Link to="/dashboard/billing/invoices" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
              <ChevronLeftIcon className="size-3" />Back to invoices
            </Link>
          </div>
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
            <FileTextIcon className="size-10 text-muted-foreground/40" />
            <p className="text-sm font-medium text-foreground">Invoice not found</p>
          </div>
        </div>
      )
    }

    const inv = detail.invoice
    const statusInfo = INVOICE_STATUS_STYLES[inv.status] ?? { label: inv.status, color: 'bg-muted text-muted-foreground' }

    return (
      <div className="mx-auto max-w-4xl">
        <div className="mb-6">
          <Link to="/dashboard/billing/invoices" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
            <ChevronLeftIcon className="size-3" />Back to invoices
          </Link>
        </div>

        {/* Invoice Header */}
        <div className="mb-6 rounded-xl border border-border bg-card p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <FileTextIcon className="size-6" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-semibold text-foreground">{inv.invoice_number}</h1>
                  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${statusInfo.color}`}>
                    {inv.status === 'paid' && <CheckCircleIcon className="size-3" />}
                    {inv.status === 'overdue' && <AlertTriangleIcon className="size-3" />}
                    {inv.status === 'pending' && <ClockIcon className="size-3" />}
                    {inv.status === 'cancelled' && <XCircleIcon className="size-3" />}
                    {statusInfo.label}
                  </span>
                </div>
                {/* F4: buyer legal name (snapshot, falls back to org_name) */}
                <p className="mt-1 text-sm text-muted-foreground">{inv.buyer?.legal_name || inv.org_name}</p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-right">
                <p className="text-2xl font-bold tabular-nums">{formatCentsWithCurrency(inv.amount_due_cents, inv.currency)}</p>
                <p className="text-xs text-muted-foreground">{inv.currency}</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleDownloadPdf}>
                <DownloadIcon className="mr-1.5 size-3.5" />
                Download PDF
              </Button>
            </div>
          </div>
        </div>

        {/* F4: Seller + Buyer identity blocks (legally valid B2B invoice) */}
        <div className="mb-6 grid gap-6 sm:grid-cols-2">
          <IdentityBlock
            title="Seller"
            icon={<StoreIcon className="size-4" />}
            name={inv.seller?.legal_name || 'PeerPass'}
            lines={
              [
                inv.seller?.registration_country && inv.seller.registration_country.toUpperCase(),
                inv.seller?.registration_number && `Registration: ${inv.seller.registration_number}`,
                inv.seller?.vat_number && `VAT: ${inv.seller.vat_number}`,
                inv.seller?.registered_address,
                inv.seller?.contact_email,
              ].filter(Boolean) as string[]
            }
          />
          <IdentityBlock
            title="Bill to"
            icon={<BuildingIcon className="size-4" />}
            name={inv.buyer?.legal_name || inv.org_name}
            lines={
              [
                inv.buyer?.registration_country && inv.buyer.registration_country.toUpperCase(),
                inv.buyer?.vat_number && `VAT: ${inv.buyer.vat_number}`,
                inv.buyer?.registration_number && `Registration: ${inv.buyer.registration_number}`,
                inv.buyer?.registered_address,
              ].filter(Boolean) as string[]
            }
          />
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Invoice Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Fields */}
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">Invoice details</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <DetailField icon={<HashIcon className="size-4" />} label="Invoice number" value={inv.invoice_number} />
                <DetailField icon={<CalendarIcon className="size-4" />} label="Period" value={`${formatDate(inv.period_start)} — ${formatDate(inv.period_end)}`} />
                <DetailField icon={<UsersIcon className="size-4" />} label="Active users" value={String(inv.total_active_users)} />
                <DetailField icon={<BanknoteIcon className="size-4" />} label="Subtotal" value={formatCentsWithCurrency(inv.subtotal_cents, inv.currency)} />
                <DetailField icon={<CalendarIcon className="size-4" />} label="Due date" value={formatDate(inv.due_date)} />
                {inv.paid_at && <DetailField icon={<CheckCircleIcon className="size-4 text-emerald-500" />} label="Paid at" value={formatDate(inv.paid_at)} />}
              </div>
            </div>

            {/* Line Items */}
            {detail.line_items.length > 0 && (
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">Line items</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                      <th className="pb-2 pr-4">Item</th>
                      <th className="pb-2 pr-4">Qty</th>
                      <th className="pb-2 pr-4 text-right">Unit price</th>
                      <th className="pb-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.line_items.map((item) => (
                      <tr key={item.item_id} className={`border-b border-border/50 last:border-0 ${item.item_type === 'add_on' ? 'bg-amber-500/[0.03]' : ''}`}>
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-foreground">{item.description}</p>
                            {item.item_type === 'add_on' && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
                                <PlusCircleIcon className="size-2.5" />
                                Add-on
                              </span>
                            )}
                          </div>
                          <p className="text-[10px] text-muted-foreground">{item.item_type.replace(/_/g, ' ')}</p>
                        </td>
                        <td className={`py-3 pr-4 text-xs tabular-nums ${item.item_type === 'add_on' ? 'text-amber-600 dark:text-amber-400' : ''}`}>{item.quantity ?? '—'}</td>
                        <td className={`py-3 pr-4 text-right text-xs tabular-nums ${item.item_type === 'add_on' ? 'text-amber-600 dark:text-amber-400' : ''}`}>
                          {item.unit_price_cents != null ? formatCentsWithCurrency(item.unit_price_cents, inv.currency) : '—'}
                        </td>
                        <td className={`py-3 text-right text-sm font-semibold tabular-nums ${item.item_type === 'add_on' ? 'text-amber-600 dark:text-amber-400' : ''}`}>{formatCentsWithCurrency(item.amount_cents, inv.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3} className="pt-3 pr-4 text-right text-xs font-medium text-muted-foreground">Total</td>
                      <td className="pt-3 text-right text-base font-bold tabular-nums">{formatCentsWithCurrency(inv.amount_due_cents, inv.currency)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* Payment Receipts */}
            {detail.payment_receipts.length > 0 && (
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">Payment receipts</h3>
                <div className="space-y-2">
                  {detail.payment_receipts.map((receipt) => (
                    <div key={receipt.receipt_id} className="flex items-center gap-3 rounded-lg bg-muted/50 px-3 py-2">
                      <CheckCircleIcon className="size-4 shrink-0 text-emerald-500" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground">{formatCentsWithCurrency(receipt.amount_cents, receipt.currency)}</p>
                        <p className="text-xs text-muted-foreground">
                          Received {formatDate(receipt.received_date)}
                          {receipt.reference_number && ` · Ref: ${receipt.reference_number}`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar: Payment Options + Bank Details */}
          <div className="space-y-4">
            {/* Card payment button — Stripe Checkout */}
            {(inv.status === 'pending' || inv.status === 'overdue') && (
              <div className="rounded-xl border border-border bg-card p-5">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <CreditCardIcon className="size-4" />
                  </div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">Pay with card</h3>
                </div>
                <p className="mb-4 text-xs text-muted-foreground">
                  Pay this invoice instantly with a credit or debit card via Stripe.
                </p>
                <Button
                  onClick={handlePayWithCard}
                  disabled={paying}
                  className="w-full"
                >
                  {paying ? (
                    <>
                      <LoaderIcon className="mr-2 size-4 animate-spin" />
                      Redirecting to checkout…
                    </>
                  ) : (
                    <>
                      <ExternalLinkIcon className="mr-2 size-4" />
                      Pay {formatCentsWithCurrency(inv.amount_due_cents, inv.currency)} by card
                    </>
                  )}
                </Button>
                <p className="mt-2 text-[10px] text-muted-foreground/60">
                  You will be redirected to Stripe's secure checkout page.
                </p>
              </div>
            )}

            {inv.status !== 'paid' && bankDetails && (
              <div className="rounded-xl border border-border bg-card p-5">
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex size-8 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                    <LandmarkIcon className="size-4" />
                  </div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">Bank transfer details</h3>
                </div>

                <div className="space-y-3">
                  <CopyField
                    label="Bank name"
                    value={bankDetails.bank_name}
                    fieldKey="bank_name"
                    copyField={copyField}
                    onCopy={handleCopy}
                  />
                  <CopyField
                    label="Account holder"
                    value={bankDetails.account_holder}
                    fieldKey="account_holder"
                    copyField={copyField}
                    onCopy={handleCopy}
                  />
                  {bankDetails.account_number_masked && (
                    <CopyField
                      label="Account number"
                      value={bankDetails.account_number ?? bankDetails.account_number_masked ?? ''}
                      fieldKey="account_number"
                      copyField={copyField}
                      onCopy={handleCopy}
                    />
                  )}
                  {bankDetails.iban_masked && (
                    <CopyField
                      label="IBAN"
                      value={bankDetails.iban ?? bankDetails.iban_masked ?? ''}
                      fieldKey="iban"
                      copyField={copyField}
                      onCopy={handleCopy}
                    />
                  )}
                  {bankDetails.swift_bic && (
                    <CopyField
                      label="SWIFT/BIC"
                      value={bankDetails.swift_bic}
                      fieldKey="swift"
                      copyField={copyField}
                      onCopy={handleCopy}
                    />
                  )}
                  {bankDetails.currency && (
                    <div className="flex items-center gap-2">
                      <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                        <CreditCardIcon className="size-3.5" />
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Currency</p>
                        <p className="text-sm font-medium">{bankDetails.currency}</p>
                      </div>
                    </div>
                  )}
                </div>

                {bankDetails.notes && (
                  <p className="mt-3 text-xs text-muted-foreground">{bankDetails.notes}</p>
                )}
              </div>
            )}

            {inv.status === 'paid' && (
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5">
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="size-8 text-emerald-500" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">Paid</p>
                    <p className="text-xs text-emerald-600/70 dark:text-emerald-400/70">
                      {inv.paid_at ? `Received ${formatDate(inv.paid_at)}` : 'Payment confirmed'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  },
})

function IdentityBlock({ title, icon, name, lines }: { title: string; icon: React.ReactNode; name: string; lines: string[] }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
        <span className="text-primary">{icon}</span>
        {title}
      </div>
      <p className="text-sm font-semibold text-foreground">{name}</p>
      {lines.length > 0 && (
        <div className="mt-2 space-y-0.5">
          {lines.map((line) => (
            <p key={line} className="text-xs text-muted-foreground">{line}</p>
          ))}
        </div>
      )}
    </div>
  )
}

function DetailField({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">{icon}</div>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">{label}</p>
        <p className="truncate text-sm font-medium text-foreground">{value}</p>
      </div>
    </div>
  )
}

function CopyField({ label, value, fieldKey, copyField, onCopy }: {
  label: string; value: string; fieldKey: string; copyField: string | null; onCopy: (val: string, key: string) => void
}) {
  const isCopied = copyField === fieldKey
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">{label}</p>
      <div className="mt-0.5 flex items-center gap-1.5">
        <code className="flex-1 break-all rounded-md bg-muted/50 px-2 py-1 text-xs font-mono text-foreground">{value}</code>
        <button
          type="button"
          onClick={() => onCopy(value, fieldKey)}
          className="flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          title={`Copy ${label}`}
        >
          {isCopied ? <CheckIcon className="size-3 text-emerald-500" /> : <CopyIcon className="size-3" />}
        </button>
      </div>
    </div>
  )
}
